/** @jsxImportSource theme-ui */
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { SxProp } from 'theme-ui';

import { InsightsState } from 'app-redux/InsightsState';
import * as dto from 'client/dto';
import { TimeFrameEnumType } from 'models/TimeframeModels';
import theme from 'theme';
import { DefaultTimeframeStartDateType, SpeciesIdHuman } from 'utils/constants';
import { formatDate, getMomentAgo } from 'utils/dateTimeHelpers';
import { simpleTimeFrameDescription } from 'utils/modelHelpers';

import { BdIcon } from 'components/_common/BdIcon';
import { BdCaseIncidenceIcon } from 'components/_common/Icon/BdCaseIncidenceIcon';
import { Typography } from 'components/_common/Typography';
import BdPrimaryLink from 'components/_controls/BdLink/BdPrimaryLink';

import diseasesSelectors from 'diseases/diseases.selectors';
import locationsSelectors from 'locations/locations.selectors';
import { fetchIncidenceData } from 'riskOverview/riskOverview.saga';
import riskOverviewSelectors from 'riskOverview/riskOverview.selectors';

import AoiVsCountrySparkGraph from './AoiVsCountrySparkGraph';
import CaseIncidenceSummarySkeleton from './CaseIncidenceSummarySkeleton';
import CaseIncidenceDisclosure from './detailed/CaseIncidenceDisclosure';
import RiskOverviewCardGeotreeCaseDetails from './RiskOverviewCardGeotreeCaseDetails';

const GraphSvg: React.FC<{ fillColor: string }> = ({ fillColor }) => {
  return (
    <div sx={{ width: '13px', height: '7px', display: 'inline-block', ml: '4px', mr: '2px' }}>
      <svg width="100%" height="100%" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect y="3" width="3" height="4" fill={fillColor} />
        <rect x="5" width="3" height="7" fill={fillColor} />
        <rect x="10" y="4" width="3" height="3" fill={fillColor} />
      </svg>
    </div>
  );
};

type RiskOverviewCardActivityDetailsProps = SxProp & {
  diseaseActivityId: number;
  locationId: number;
  timeFrame: TimeFrameEnumType;
  noDataLoad?: boolean;
  graphWidth?: number;
};

type AoiVsCountryAoiLegendProps = {
  aoi: string;
  countryAoi: string;
  timeFrame: TimeFrameEnumType;
};
const AoiVsCountryAoiLegend: React.FC<AoiVsCountryAoiLegendProps> = ({
  aoi,
  countryAoi,
  timeFrame,
}) => {
  return (
    <div
      className="aoi-vs-country-legend"
      sx={{
        display: 'inline-block',
        marginTop: ['24px'],
        wordBreak: 'break-word',
        width: '100%',
      }}
    >
      <Typography variant="body2" marginBottom="0" inline>
        Cases in
      </Typography>
      <GraphSvg fillColor={theme.colors.seafoam80} />
      {aoi !== countryAoi && countryAoi?.length ? (
        <>
          <Typography
            variant="subtitle2"
            color="seafoam100"
            marginBottom="0"
            inline
            sx={{ marginTop: '1px' }}
          >
            {aoi}&nbsp;
          </Typography>
          <Typography variant="body2" marginBottom="0" inline>
            vs.
          </Typography>
          <GraphSvg fillColor={theme.colors.sea50} />
          <Typography variant="body2" marginBottom="0" inline>
            {countryAoi}&nbsp;
          </Typography>
        </>
      ) : (
        <Typography
          variant="subtitle2"
          color="seafoam100"
          marginBottom="0"
          inline
          sx={{ marginTop: '1px' }}
        >
          {aoi}&nbsp;
        </Typography>
      )}
      <Typography variant="body2" marginBottom="0" inline>
        in the&nbsp;
      </Typography>
      <Typography variant="subtitle2" marginBottom="0" sx={{ marginTop: '1px' }} inline>
        {simpleTimeFrameDescription(timeFrame, null).toLocaleLowerCase()}
      </Typography>
    </div>
  );
};

const RiskOverviewCardActivityDetails: React.FC<RiskOverviewCardActivityDetailsProps> = ({
  locationId,
  diseaseActivityId,
  timeFrame,
  noDataLoad = false,
  graphWidth = 194,
  ...props
}) => {
  const dispatch = useDispatch();
  const startDate = getMomentAgo(DefaultTimeframeStartDateType).toISOString();
  const riskFeedEntry: dto.RiskFeedEntryModel = useSelector((state: InsightsState) =>
    riskOverviewSelectors.getRiskFeedEntryByActivityIdAndLocationId(state)
  )(diseaseActivityId, locationId);

  const isLoading: boolean = useSelector<InsightsState, boolean>(
    riskOverviewSelectors.isLoadingRiskIncidenceByDiseaseIdAndLocationId(
      riskFeedEntry?.diseaseId,
      riskFeedEntry?.locationId
    )
  );
  const lastCheckedDateForCountry = useSelector(
    riskOverviewSelectors.getLastUpdatedByDiseaseIdAndLocationId(
      riskFeedEntry?.diseaseId,
      locationId
    )
  );

  const countryAoi = useSelector(
    riskOverviewSelectors.getCountryFromNestedCasesTree(riskFeedEntry?.diseaseId, locationId)
  );
  useEffect(() => {
    if (riskFeedEntry?.diseaseId && !noDataLoad) {
      dispatch(
        fetchIncidenceData({
          diseaseId: riskFeedEntry?.diseaseId,
          locationId: locationId,
          startDate: startDate,
        })
      );
    }
  }, [riskFeedEntry, noDataLoad]);

  const locationName: string = useSelector<InsightsState, string>(
    locationsSelectors.getLocationName(riskFeedEntry?.locationId)
  );
  const diseaseName: string = useSelector<InsightsState, string>(
    diseasesSelectors.getDiseaseName(riskFeedEntry?.diseaseId)
  );
  const hasIncidenceCaseData = useSelector(
    riskOverviewSelectors.hasIncidenceCaseData(riskFeedEntry?.diseaseId, locationId)
  );

  return (
    <div data-testid="card-activity-details" sx={{ marginTop: '8px' }} {...props}>
      <BdCaseIncidenceIcon
        caseCountPrevalence={riskFeedEntry?.caseIncidence?.casesPerPopulation}
        isCaseDataAvailable={riskFeedEntry?.caseIncidence?.hasReportedData}
        isCaseDataInherited={riskFeedEntry?.caseIncidence?.isInherited}
        timeframe={`(${simpleTimeFrameDescription(timeFrame, null).toLocaleLowerCase()})`}
        dataLabelVariant="h6"
      />
      {riskFeedEntry?.caseIncidence?.isInherited && (
        <CaseIncidenceDisclosure
          locationName={locationName}
          parentLocationName={riskFeedEntry?.caseIncidence?.parentLocationName}
        />
      )}
      <AoiVsCountryAoiLegend
        aoi={locationName}
        countryAoi={countryAoi?.locationName}
        timeFrame={timeFrame}
      />
      {isLoading ? (
        <CaseIncidenceSummarySkeleton timeFrame={timeFrame} />
      ) : (
        <>
          <div
            className="ro-activity-cases-container"
            sx={{
              display: ['block', 'flex'],
              marginTop: ['12px', '16px'],
            }}
          >
            <AoiVsCountrySparkGraph
              timeFrame={timeFrame}
              locationId={riskFeedEntry?.locationId}
              diseaseId={riskFeedEntry?.diseaseId}
              graphWidth={graphWidth}
              sx={{
                width: '100%',
                display: ['flex', 'block'],
                justifyContent: 'center',
                backgroundColor: t => [t.colors.deepSea10, 'white'],
              }}
            />
            <RiskOverviewCardGeotreeCaseDetails
              diseaseId={riskFeedEntry?.diseaseId}
              locationId={locationId}
            />
          </div>
          {countryAoi && (
            <>
              {!!lastCheckedDateForCountry && (
                <div id="roc-activty-last-checked" sx={{ mt: '24px' }}>
                  <Typography variant="caption" color="stone50" marginBottom="0">
                    Activity in {countryAoi?.locationName} last checked on{' '}
                    {formatDate(lastCheckedDateForCountry, 'MMMM d, yyyy')}
                  </Typography>
                </div>
              )}
              {hasIncidenceCaseData && (
                <BdPrimaryLink
                  data-html2canvas-ignore="true"
                  data-testid="activity-history-link"
                  variant="body1"
                  href={`/location/${locationId}/disease/${riskFeedEntry?.diseaseId}/species/${SpeciesIdHuman}/activity`}
                  sx={{ mt: ['22px', '30px'], ml: ['-2px'] }}
                >
                  <div>
                    See activity history for {diseaseName} in {countryAoi?.locationName}{' '}
                    <BdIcon
                      name="icon-chevron-right"
                      sx={{
                        '&.icon.bd-icon': { margin: '0', fontWeight: 'bold', fontSize: '12px' },
                      }}
                    />
                  </div>
                </BdPrimaryLink>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
};

export default RiskOverviewCardActivityDetails;
